<template>
  <div data-test="orderHistory" class="my-6">
    <AppFilterNavigation no-preserve class="mb-4">
      <template #default="prop">
        <div class="col-12 col-md-2 px-2 my-2 text-center">
          <AppSelectMultipleDates
            :date-from.sync="filters.dateFrom"
            :date-until.sync="filters.dateUntil"
            data-test="dates"
            ignore-default-date
            clearable
            :date-options="dateOptions"
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            :label="
              $t(
                'people:ShoppingBasket.OrderBalance.Date.label',
                'Date',
                'Date label in Shopping basket balance page'
              )
            "
          />
        </div>
        <div class="col-12 col-md-2 px-2 my-2 text-center">
          <AppGenericSelect
            v-model="filters.statuses"
            :items="statuses"
            multiple
            :is-filter="!prop.mobile"
            :dense="prop.mobile"
            clearable
            :label="
              $t(
                'people:ShoppingBasket.OrderBalance.Statuses.label',
                'Statuses',
                'Statuses  label in Shopping basket balance page'
              )
            "
            item-value="id"
            item-text="name"
          />
        </div>
        <div class="col-12 col-md-8 px-2 my-2 text-center">
          <AppSearchBox
            :value.sync="filters.search"
            clearable
            :input-label="
              $t(
                'people:ShoppingBasket.OrderHistory.Search.label',
                'Search by keyword or Order ID',
                'Search label in Shopping basket history page'
              )
            "
          />
        </div>
      </template>
    </AppFilterNavigation>

    <v-card id="ordersHistory" color="primary" class="fipa--header" tile />
    <v-data-table
      :loading="
        hasPending ||
        $apollo.queries.person.loading ||
        $apollo.queries.shoppingBasket.loading
      "
      :items="paymentTransactions"
      :headers="headers"
      :headers-length="99"
      :hide-default-header="$vuetify.breakpoint.smAndDown"
      disable-sort
      :items-per-page="-1"
      hide-default-footer
      class="entsys--table"
    >
      <template #item="{ item, isExpanded, expand }">
        <PaymentHistory
          :item="item"
          :mutation-loading="loading"
          @confirm="$event => paymentConfirm($event)"
          @cancel="$event => paymentCancel($event)"
        >
          <template #actions>
            <button
              class="btn-focus"
              :aria-label="$t('common:expandButton.alt')"
              @click.prevent="() => expand(!isExpanded)"
            >
              <v-icon color="primary">
                {{ isExpanded ? "$iconCircleCaretUp" : "$iconCircleCaretDown" }}
              </v-icon>
            </button>
          </template>
        </PaymentHistory>
      </template>
      <template #expanded-item="{ item }">
        <ExpandedPaymentHistory
          :item="item"
          :transaction-id="item.id"
          :mutation-loading="loading"
          @confirm="$event => paymentConfirm($event)"
          @cancel="$event => paymentCancel($event)"
        />
      </template>
    </v-data-table>

    <div class="text-right">
      <AppLoadMoreBtn
        :has-more-results="hasMoreResults"
        :loading="$apollo.queries.person.loading"
        @loadMore="loadMore"
      />
    </div>
  </div>
</template>
<script>
import gql from "fraql";
import AppReceiptLink from "@/components/elements/AppReceiptLink.vue";

import AppGenericSelect from "@/components/elements/AppSelects/AppGenericSelect.vue";
import AppSearchBox from "@/components/elements/AppSearchBox.vue";
import AppSelectMultipleDates from "@/components/elements/AppSelects/AppSelectMultipleDates.vue";
import AppFilterNavigation from "@/components/elements/AppFilterNavigation.vue";
import AppLoadMoreBtn from "@/components/elements/AppLoadMoreBtn.vue";

import AppShowEventDetails from "@/components/elements/AppShows/event/AppShowEventDetails.vue";

import PaymentHistory from "./elements/PaymentHistory.vue";
import ExpandedPaymentHistory from "./elements/ExpandedPaymentHistory.vue";

import AppShowPaymentStatus from "@/components/elements/AppShows/order/AppShowPaymentStatus.vue";
import AppOrganisationNameLink from "@/components/elements/AppNameLinks/AppOrganisationNameLink.vue";
import AppShowHorseName from "@/components/elements/AppShows/horse/AppShowHorseName.vue";

const PER_PAGE = 15;

export default {
  name: "OrdersHistory",
  components: {
    AppGenericSelect,
    AppSelectMultipleDates,
    AppSearchBox,
    AppLoadMoreBtn,
    AppFilterNavigation,
    PaymentHistory,
    ExpandedPaymentHistory
  },
  props: {
    personId: {
      type: String,
      default: null
    },
    shoppingBasketId: {
      type: String,
      default: null
    },
    refetchHistory: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      transactionsPolling: {},
      hasMoreResults: true,
      filters: {
        search: "",
        dateFrom: null,
        dateUntil: null,
        statuses: [],
        categories: []
      },
      loading: false,
      paymentTransactions: []
    };
  },
  apollo: {
    person: {
      query: gql`
        query paymentTransactions(
          $id: ID!
          $offset: Int
          $limit: Int
          $sortDesc: Boolean
          $sortBy: String
          $keyword: String
          $statuses: [String]
          $categories: [String]
          $createdAtFrom: String
          $createdAtUntil: String
          ) {
          person(id: $id) {
            id
            payments(
              offset: $offset
              limit: $limit
              sortBy: $sortBy
              keyword: $keyword
              sortDesc: $sortDesc
              statuses: $statuses
              categories: $categories
              createdAtFrom: $createdAtFrom
              createdAtUntil: $createdAtUntil
            ) {
              id
              referenceCode
              status
              processor
              amountCents
              amountRefundedCents
              allowedMutations
              receiptsStatus
              receipts {
                ${AppReceiptLink.fragments.receipt}
              }
              ${PaymentHistory.fragments.paymentFields}
              payables {
                id
                productCodename
                productParams
                status
                productId
                product {
                  id
                  name
                  ${PaymentHistory.fragments.productFields}
                }
                ${PaymentHistory.fragments.payableFields}
                relatedEvent {
                  ${AppShowEventDetails.fragments.event}
                }
                recipientOrganisation {
                  ${AppOrganisationNameLink.fragments.organisation}
                }
                organisation {
                  ${AppOrganisationNameLink.fragments.organisation}
                }
                horse {
                  ${AppShowHorseName.fragments.horse}
                }
                horseApplication {
                  id
                  horseBirthName
                  horse {
                    ${AppShowHorseName.fragments.horse}
                  }
                }
              }
              refundedAt
              createdAt
              ${AppShowPaymentStatus.fragments.payment}
            }
          }
        }
      `,
      manual: true,
      skip() {
        if (!this.personId) return true;
        return false;
      },
      variables() {
        return {
          id: this.personId,
          offset: 0,
          limit: PER_PAGE,
          sortBy: "createdAt",
          sortDesc: true,
          keyword: this.filters.search,
          statuses: this.filters.statuses,
          createdAtFrom: this.filters.dateFrom,
          createdAtUntil: this.filters.dateUntil,
          categories: this.filters.categories
        };
      },
      result({ loading, data }) {
        if (loading || !data || !data.person) return;
        const { person } = data;
        this.hasMoreResults = person.payments.length >= PER_PAGE;

        this.paymentTransactions = JSON.parse(
          JSON.stringify(person.payments)
        ).filter(Boolean);

        const pendingTransactionIds = this.paymentTransactions
          .filter(
            tran =>
              (tran.status == "PENDING" &&
                !["MANUAL_CREDIT_TRANSFER", "MANUAL"].includes(
                  tran.processor
                )) ||
              tran.receiptsStatus == "GENERATING"
          )
          .map(x => x.id);

        if (pendingTransactionIds.length > 0) {
          this.pollingPendingTransactions(pendingTransactionIds);
        }
      },
      fetchPolicy: "cache-and-network"
    },
    shoppingBasket: {
      query: gql`
        query paymentTransactionsAnonymouse(
          $id: ID!
          $offset: Int
          $limit: Int
          $sortDesc: Boolean
          $sortBy: String
          $keyword: String
          $statuses: [String]
          $createdAtFrom: String
          $createdAtUntil: String
          $categories: [String]
        ) {
          shoppingBasket(id: $id) {
            id
            payments(
              offset: $offset
              limit: $limit
              sortBy: $sortBy
              keyword: $keyword
              sortDesc: $sortDesc
              statuses: $statuses
              createdAtFrom: $createdAtFrom
              createdAtUntil: $createdAtUntil
              categories: $categories
            ) {
              id
              referenceCode
              status
              processor
              amountCents
              allowedMutations
              receiptsStatus
              receipts {
                ${AppReceiptLink.fragments.receipt}
              }
              ${PaymentHistory.fragments.paymentFields}
              payables {
                id
                productCodename
                productParams
                productId
                product {
                  id
                  name
                }
                relatedEvent {
                  ${AppShowEventDetails.fragments.event}
                }
                recipientOrganisation {
                  ${AppOrganisationNameLink.fragments.organisation}
                }
                organisation {
                  ${AppOrganisationNameLink.fragments.organisation}
                }
                horse {
                  ${AppShowHorseName.fragments.horse}
                }
                horseApplication {
                  id
                  horseBirthName
                  horse {
                    id
                    name
                  }
                }
              }
              createdAt
              ${AppShowPaymentStatus.fragments.payment}

            }
          }
        }
      `,
      manual: true,
      skip() {
        if (!this.shoppingBasketId) return true;
        return false;
      },
      variables() {
        return {
          id: this.shoppingBasketId,
          offset: 0,
          limit: PER_PAGE,
          sortBy: "createdAt",
          sortDesc: true,
          keyword: this.filters.search,
          statuses: this.filters.statuses,
          createdAtFrom: this.filters.dateFrom,
          createdAtUntil: this.filters.dateUntil,
          categories: this.filters.categories
        };
      },
      result({ loading, data }) {
        if (loading || !data || !data.shoppingBasket) return;
        const { shoppingBasket } = data;

        this.hasMoreResults = shoppingBasket.payments.length >= PER_PAGE;

        this.paymentTransactions = JSON.parse(
          JSON.stringify(shoppingBasket.payments)
        ).filter(Boolean);

        const pendingTransactionIds = this.paymentTransactions
          .filter(
            tran =>
              (tran.status == "PENDING" &&
                !["MANUAL_CREDIT_TRANSFER", "MANUAL"].includes(
                  tran.processor
                )) ||
              tran.receiptsStatus == "GENERATING"
          )
          .map(x => x.id);

        if (pendingTransactionIds.length > 0) {
          this.pollingPendingTransactions(pendingTransactionIds);
        }
      }
    }
  },
  computed: {
    statuses() {
      return [
        { id: "PENDING", name: this.$t("common:status.PENDING") },
        { id: "PAID", name: this.$t("common:status.PAID") },
        { id: "CANCELLED", name: this.$t("common:status.CANCELLED") }
      ];
    },
    hasPending() {
      return Object.values(this.transactionsPolling).filter(Boolean).length > 0;
    },
    defaultDate() {
      return [this.$dayjs().format("YYYY") + "-01-01"];
    },
    headers() {
      return [
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Date.label",
            "Date"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Transaction.label",
            "Transaction"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Details.label",
            "Details"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Status.label",
            "Status"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Amount.label",
            "Amount"
          ),
          sortable: false
        },
        {
          text: this.$t(
            "people:ShoppingBasketPage.TransactionsHistoryTab.table.heading.Receipt.label",
            "Receipt"
          ),
          sortable: false
        },
        { text: "", sortable: false }
      ];
    },
    dateOptions() {
      const year = this.$dayjs().format("YYYY");
      return [
        {
          type: "custom",
          text: this.$dayjs().subtract(1, "months").format("MMMM"),
          dateFrom: this.$dayjs()
            .subtract(1, "months")
            .startOf("month")
            .format("YYYY-MM-DD"),
          dateUntil: this.$dayjs()
            .subtract(1, "months")
            .endOf("month")
            .format("YYYY-MM-DD")
        },
        { type: "wholeYear" },
        {
          type: "custom",
          text: year - 1,
          dateFrom: year - 1 + "-01-01",
          dateUntil: year - 1 + "-12-31"
        }
      ];
    }
  },
  watch: {
    refetchHistory() {
      if (this.personId) {
        this.$apollo.queries.person.refetch();
      } else if (this.shoppingBasketId) {
        this.$apollo.queries.shoppingBasket.refetch();
      }
    }
  },
  beforeDestroy() {
    Object.values(this.transactionsPolling)
      .filter(Boolean)
      .forEach(polling => {
        clearInterval(polling);
      });
  },
  methods: {
    pollingPendingTransactions(paymentIds) {
      paymentIds.forEach(paymentId => {
        if (this.transactionsPolling[paymentId]) return;
        this.$set(this.transactionsPolling, paymentId, null);

        this.transactionsPolling[paymentId] = setInterval(async () => {
          const { data } = await this.$apollo.query({
            query: gql`
              query paymentPing(
                $id: ID!
                $shoppingBasketId: ID
              ) {
                payment(
                  id: $id
                  shoppingBasketId: $shoppingBasketId
                ) {
                  id
                  status
                  receiptsStatus
                  receipts {
                    ${AppReceiptLink.fragments.receipt}
                  }
                  invoice {
                    id
                    ${PaymentHistory.fragments.invoiceFields}
                  }
                }
              }
            `,
            variables: {
              id: paymentId,
              shoppingBasketId: sessionStorage.getItem("shoppingBasketId")
            }
          });

          if (
            data?.payment &&
            data.payment.status != "PENDING" &&
            data.payment.receiptsStatus != "GENERATING"
          ) {
            clearInterval(this.transactionsPolling[paymentId]);
            this.transactionsPolling[paymentId] = null;
          }
        }, 15000);
      });
    },
    paymentCancel({ item, evt }) {
      this.loading = true;
      this.$mutateLoading.disableButton(evt.target, "icon");
      this.$mutateLoading.showLoading(evt.target);
      this.$apollo
        .mutate({
          mutation: gql`
            mutation paymentCancel($paymentId: ID!) {
              paymentCancel(input: { paymentId: $paymentId }) {
                payment {
                  id
                }
              }
            }
          `,
          variables: {
            paymentId: item.id
          }
        })
        .then(() => {
          this.$apollo.queries.person.refetch();
          this.$apollo.queries.shoppingBasket.refetch();
          this.$root.$snackbar("success");
          this.loading = false;
          this.$mutateLoading.enableButton(evt.target, "icon");
          this.$mutateLoading.hideLoading(evt.target);
          //this.$apollo.queries.person.refetch();
          this.$emit("refetch");
        })
        .catch(err => {
          this.loading = false;
          this.$mutateLoading.enableButton(evt.target, "icon");
          this.$mutateLoading.hideLoading(evt.target);
          this.$root.$snackbarServerError(err, { level: "error" });
        });
    },
    paymentConfirm({ item, evt }) {
      this.loading = true;
      this.$mutateLoading.disableButton(evt.target, "icon");
      this.$mutateLoading.showLoading(evt.target);
      this.$apollo
        .mutate({
          mutation: gql`
            mutation paymentConfirm($paymentId: ID!) {
              paymentConfirm(input: { paymentId: $paymentId }) {
                payment {
                  id
                }
              }
            }
          `,
          variables: {
            paymentId: item.id
          }
        })
        .then(() => {
          this.$apollo.queries.person.refetch();
          this.$apollo.queries.shoppingBasket.refetch();
          this.$root.$snackbar("success");
          this.loading = false;
          this.$mutateLoading.enableButton(evt.target, "icon");
          this.$mutateLoading.hideLoading(evt.target);
          this.$emit("refetch");
        })
        .catch(err => {
          this.loading = false;
          this.$mutateLoading.enableButton(evt.target, "icon");
          this.$mutateLoading.hideLoading(evt.target);
          this.$root.$snackbarServerError(err, { level: "error" });
        });
    },
    loadMore() {
      if (this.personId) {
        this.$apollo.queries.person
          .fetchMore({
            variables: {
              offset: this.paymentTransactions.length
            }
          })
          .then(({ data: { person } }) => {
            this.hasMoreResults = person.payments.length >= PER_PAGE;

            this.paymentTransactions = [
              ...this.paymentTransactions,
              ...JSON.parse(JSON.stringify(person.payments)).filter(Boolean)
            ];
          });
      } else {
        this.$apollo.queries.shoppingBasket
          .fetchMore({
            variables: {
              offset: this.paymentFields.length
            }
          })
          .then(({ data: { shoppingBasket } }) => {
            this.hasMoreResults =
              shoppingBasket.paymentTransactions.length >= PER_PAGE;

            this.paymentTransactions = [
              ...this.paymentTransactions,
              ...JSON.parse(JSON.stringify(shoppingBasket.payments)).filter(
                Boolean
              )
            ];
          });
      }
    }
  }
};
</script>
